class SennaTheme {
  constructor() {
    this.init();
  }

  init() {
    this.initCartDrawer();
    this.initMobileMenu();
    this.initAddToCartForms();
    this.initGalleryDotsObserver();
  }

  // Cart Drawer Controller
  initCartDrawer() {
    const getDrawer = () => document.querySelector('.cart-drawer') || document.querySelector('#CartDrawer');
    const getOverlay = () => document.querySelector('.cart-drawer-overlay');

    const openDrawer = () => {
      const drawer = getDrawer();
      const overlay = getOverlay();
      if (drawer) drawer.classList.add('is-active');
      if (overlay) overlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    };

    const closeDrawer = () => {
      const drawer = getDrawer();
      const overlay = getOverlay();
      if (drawer) drawer.classList.remove('is-active');
      if (overlay) overlay.classList.remove('is-active');
      document.body.style.overflow = '';
    };

    // Global Click Handler for Triggers & Close Buttons
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-cart-drawer-trigger]');
      if (trigger) {
        e.preventDefault();
        openDrawer();
        return;
      }

      const closeBtn = e.target.closest('[data-cart-drawer-close]');
      if (closeBtn) {
        e.preventDefault();
        closeDrawer();
        return;
      }

      const overlay = e.target.closest('.cart-drawer-overlay');
      if (overlay) {
        e.preventDefault();
        closeDrawer();
        return;
      }
    });

    // Mobile Product Card Size Popout Click-Outside Dismiss
    document.addEventListener('click', (e) => {
      if (window.innerWidth <= 768 && !e.target.closest('[data-fps-card]')) {
        document.querySelectorAll('[data-fps-card].is-mobile-sizes-open').forEach(card => {
          card.classList.remove('is-mobile-sizes-open');
        });
      }
    });

    // Global AJAX Item Removal Handler
    document.addEventListener('click', async (e) => {
      const removeBtn = e.target.closest('[data-cart-remove-key], [data-line-key], .cart-drawer-remove, a[href*="/cart/change"]');
      if (!removeBtn) return;

      e.preventDefault();

      const key = removeBtn.getAttribute('data-cart-remove-key') || removeBtn.getAttribute('data-line-key');
      const line = removeBtn.getAttribute('data-cart-remove-line');

      removeBtn.style.opacity = '0.3';
      removeBtn.style.pointerEvents = 'none';

      try {
        const payload = key ? { id: key, quantity: 0 } : { line: parseInt(line || 1), quantity: 0 };
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          await this.fetchCartData();
        } else {
          console.error('Failed to remove item from cart');
          removeBtn.style.opacity = '1';
          removeBtn.style.pointerEvents = 'auto';
        }
      } catch (err) {
        console.error('Cart item remove error:', err);
        removeBtn.style.opacity = '1';
        removeBtn.style.pointerEvents = 'auto';
      }
    });

    // Global AJAX Quantity Change Handler (- / +)
    document.addEventListener('click', async (e) => {
      const qtyBtn = e.target.closest('[data-cart-qty-change]');
      if (!qtyBtn) return;

      e.preventDefault();

      const delta = parseInt(qtyBtn.getAttribute('data-cart-qty-change'));
      const key = qtyBtn.getAttribute('data-cart-key');
      const line = qtyBtn.getAttribute('data-cart-line');
      const currentQty = parseInt(qtyBtn.getAttribute('data-current-qty') || '1');
      const newQty = currentQty + delta;

      if (newQty < 0) return;

      const container = qtyBtn.closest('.cart-drawer-qty-selector');
      if (container) container.style.opacity = '0.4';

      try {
        const payload = key ? { id: key, quantity: newQty } : { line: parseInt(line || 1), quantity: newQty };
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          await this.fetchCartData();
        } else {
          console.error('Failed to update item quantity');
          if (container) container.style.opacity = '1';
        }
      } catch (err) {
        console.error('Cart quantity change error:', err);
        if (container) container.style.opacity = '1';
      }
    });

    window.SennaCart = { open: openDrawer, close: closeDrawer, refresh: this.fetchCartData.bind(this) };
  }

  // Gallery Dots Observer
  initGalleryDotsObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'src') {
          const img = mutation.target;
          const card = img.closest('[data-fps-card]');
          if (!card) return;

          const galleryStr = card.getAttribute('data-gallery');
          if (!galleryStr) return;

          try {
            const gallery = JSON.parse(galleryStr);
            const currentUrl = new URL(img.src, window.location.origin).pathname;
            let activeIndex = gallery.findIndex(g => g.includes(currentUrl));
            if (activeIndex === -1) activeIndex = 0;

            const dots = card.querySelectorAll('.fps-card__dot');
            dots.forEach((dot, index) => {
              if (index === activeIndex) {
                dot.classList.add('is-active');
              } else {
                dot.classList.remove('is-active');
              }
            });
          } catch (e) { }
        }
      });
    });

    // Start observing existing and future main images
    const startObserving = () => {
      document.querySelectorAll('[data-card-main-img]').forEach(img => {
        if (!img.dataset.dotsObserved) {
          observer.observe(img, { attributes: true });
          img.dataset.dotsObserved = "true";
        }
      });
    };

    startObserving();
    // In case new cards are injected via AJAX
    const bodyObserver = new MutationObserver(startObserving);
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  // Mobile Menu Handler
  initMobileMenu() {
    const menuToggle = document.querySelector('[data-mobile-menu-toggle]');
    const nav = document.querySelector('.site-nav');

    if (menuToggle && nav) {
      menuToggle.addEventListener('click', () => {
        nav.classList.toggle('is-open');
      });
    }
  }

  // AJAX Add to Cart
  initAddToCartForms() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      e.preventDefault();
      const formData = new FormData(form);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          await this.fetchCartData();
          if (window.SennaCart) window.SennaCart.open();
        } else {
          console.error('Error adding product to cart');
        }
      } catch (err) {
        console.error('Cart submission error:', err);
      }
    });
  }

  // Fetch Cart Details & Update UI
  async fetchCartData() {
    try {
      const response = await fetch('/cart.js?t=' + Date.now(), { cache: 'no-store' });
      const cart = await response.json();

      // Update cart count badges
      const countBadges = document.querySelectorAll('[data-cart-count]');
      countBadges.forEach(badge => {
        badge.textContent = cart.item_count;
      });

      const currentDrawer = document.querySelector('#CartDrawer') || document.querySelector('.cart-drawer');
      if (currentDrawer) {
        const currentBody = currentDrawer.querySelector('.cart-drawer__body');
        let currentFooter = currentDrawer.querySelector('.cart-drawer__footer');

        if (cart.item_count > 0 && cart.items && cart.items.length > 0) {
          let itemsHtml = '<div class="cart-drawer__items">';
          cart.items.forEach((item, index) => {
            let imgUrl = item.image || (item.featured_image ? item.featured_image.url : '');
            if (imgUrl && imgUrl.startsWith('//')) imgUrl = 'https:' + imgUrl;

            const formattedPrice = (item.final_line_price / 100).toFixed(2) + ' TL';
            const title = item.product_title || item.title;
            const variantTitle = (item.variant_title && item.variant_title !== 'Default Title') ? item.variant_title : '';

            itemsHtml += `
              <div class="cart-drawer__item" style="display: flex; gap: 1rem; margin-bottom: 1rem; padding-bottom: 1rem; border-bottom: 1px solid var(--color-border);">
                ${imgUrl ? `<img src="${imgUrl}" alt="${title}" width="75" style="object-fit: contain; border-radius: var(--radius-sm); width: 75px; height: auto; flex-shrink: 0;">` : ''}
                <div style="flex-grow: 1;">
                  <h4 style="font-family: 'Inter'; font-size: 12px;margin-bottom: 0.25rem; font-weight: 500; line-height: 1.3;">${title}</h4>
                  ${variantTitle ? `<p style="font-size: 12px; color: var(--color-gray-500); margin-bottom: 0.35rem;">${variantTitle}</p>` : ''}
                  
                  <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 0.6rem; flex-wrap: wrap; gap: 8px;">
                    <div class="cart-drawer-qty-selector" style="display: inline-flex; align-items: center; border: 1px solid var(--color-border); border-radius: 4px; padding: 2px 6px; gap: 8px; background: #fafafa;">
                      <button type="button" class="cart-drawer-qty-btn" data-cart-qty-change="-1" data-cart-key="${item.key}" data-cart-line="${index + 1}" data-current-qty="${item.quantity}" aria-label="Azalt" style="background:none; border:none; padding: 2px 6px; font-size: 0.95rem; cursor:pointer; color:#333; line-height: 1;">
                        −
                      </button>
                      <span style="font-size: 0.85rem; font-weight: 600; min-width: 16px; text-align: center;">${item.quantity}</span>
                      <button type="button" class="cart-drawer-qty-btn" data-cart-qty-change="1" data-cart-key="${item.key}" data-cart-line="${index + 1}" data-current-qty="${item.quantity}" aria-label="Arttır" style="background:none; border:none; padding: 2px 6px; font-size: 0.95rem; cursor:pointer; color:#333; line-height: 1;">
                        +
                      </button>
                    </div>

                    <div style="display: flex; align-items: center; gap: 12px;">
                      <span style="font-weight: 400; font-size: 12px;">${formattedPrice}</span>
                      <button type="button" class="cart-drawer-remove" data-cart-remove-key="${item.key}" data-cart-remove-line="${index + 1}" style="background:none; border:none; padding:0; font-size: 0.8rem; color: #ef4444; text-decoration: underline; cursor: pointer;">
                        ${window.themeStrings?.remove || 'Kaldır'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            `;
          });
          itemsHtml += '</div>';

          if (currentBody) currentBody.innerHTML = itemsHtml;

          const totalPriceFormatted = (cart.total_price / 100).toFixed(2) + ' TL';

          if (!currentFooter) {
            const footerEl = document.createElement('div');
            footerEl.className = 'cart-drawer__footer';
            footerEl.style.cssText = 'padding-top: 1rem; border-top: 1px solid var(--color-border);';
            footerEl.innerHTML = `
              <p style="font-size: 0.8rem; color: #666; margin-bottom: 0.75rem; text-align: center;" data-cart-drawer-taxes-text>
                Kargo ve vergiler ödeme adımında hesaplanır.
              </p>
              <div style="display: flex; justify-content: space-between; font-weight: 600; margin-bottom: 1rem; font-size: 1rem;">
                <span>${window.themeStrings?.subtotal || 'ARA TOPLAM'}</span>
                <span data-cart-total-price>${totalPriceFormatted}</span>
              </div>
              <div class="cart-drawer-footer-actions">
                <a href="/cart" class="cart-drawer-btn-view">
                  ${window.themeStrings?.viewCart || 'SEPETİ GÖRÜNTÜLE'}
                </a>
                <form action="/cart" method="post" style="width: 100%; margin: 0;">
                  <button type="submit" name="checkout" class="cart-drawer-btn-checkout">
                    ${window.themeStrings?.checkout || 'ÖDEMEYE GEÇ'}
                  </button>
                </form>
              </div>
            `;
            currentDrawer.appendChild(footerEl);
          } else {
            const totalEl = currentFooter.querySelector('[data-cart-total-price]') || currentFooter.querySelector('span:last-child');
            if (totalEl) totalEl.textContent = totalPriceFormatted;
          }
        } else {
          if (currentBody) {
            currentBody.innerHTML = `
              <div style="text-align: center; color: var(--color-gray-500); padding: 4rem 1rem;">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" style="margin:0 auto 1rem; opacity:0.5; display:block;"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                <p style="font-size: 1rem; margin-bottom: 1.5rem;">${window.themeStrings?.cartEmpty || 'Sepetiniz henüz boş.'}</p>
                <button type="button" class="btn btn--primary" data-cart-drawer-close style="padding: 0.75rem 1.5rem; font-size: 0.85rem; letter-spacing: 0.1em;">${window.themeStrings?.continueShopping || 'ALIŞVERİŞE DEVAM ET'}</button>
              </div>
            `;
          }
          if (currentFooter) currentFooter.remove();
        }
      }

      // Event dispatched for custom hooks
      document.dispatchEvent(new CustomEvent('senna:cart:updated', { detail: { cart } }));
    } catch (err) {
      console.error('Error fetching cart:', err);
    }
  }
}

// Instant initialization handler
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.SennaApp = new SennaTheme();
  });
} else {
  window.SennaApp = new SennaTheme();
}
